import { Card } from '../ui/Card';
import { Briefcase, Clock, Users, Percent, Scale, Activity } from 'lucide-react';
import { useCurrency } from '../../hooks/useCurrency';

interface FinancialRatiosCardProps {
  grossProfitMargin: number;
  breakEvenPoint: number;
  daysSalesOutstanding: number;
  daysPayableOutstanding: number;
  workingCapital: number;
  currentRatio: number;
}

export function FinancialMetricsCard({
  grossProfitMargin,
  breakEvenPoint,
  daysSalesOutstanding,
  daysPayableOutstanding,
  workingCapital,
  currentRatio
}: FinancialRatiosCardProps) {
  const { formatConverted, baseCurrency } = useCurrency();

  // Helper pour formatter les grands nombres de manière compacte
  const formatCompactNumber = (value: number) => {
    const formatted = formatConverted(value, baseCurrency);
    const currencySymbol = baseCurrency === 'CDF' ? 'FC' : baseCurrency;
    
    // Si le nombre est très long, on peut le raccourcir
    if (formatted.length > 12) {
      if (value >= 1000000) {
        return `${(value / 1000000).toFixed(1)}M ${currencySymbol}`;
      } else if (value >= 1000) {
        return `${(value / 1000).toFixed(0)}k ${currencySymbol}`;
      }
    }
    return formatted;
  };

  const metrics = [
    {
      icon: Percent,
      title: 'Marge Brute',
      value: `${grossProfitMargin}%`,
      description: 'Rentabilité sur ventes',
      color: 'text-green-600'
    },
    {
      icon: Scale,
      title: 'Seuil de Rentabilité',
      value: formatCompactNumber(breakEvenPoint),
      description: 'Niveau d\'activité minimum',
      color: 'text-blue-600'
    },
    {
      icon: Clock,
      title: 'Délai Clients (DSO)',
      value: `${daysSalesOutstanding} jours`,
      description: 'Rotation des créances',
      color: 'text-amber-600'
    },
    {
      icon: Users,
      title: 'Délai Fournisseurs (DPO)',
      value: `${daysPayableOutstanding} jours`,
      description: 'Rotation des dettes',
      color: 'text-purple-600'
    },
    {
      icon: Briefcase,
      title: 'BFR',
      value: formatCompactNumber(workingCapital),
      description: 'Financement du cycle',
      color: 'text-indigo-600'
    },
    {
      icon: Activity,
      title: 'Ratio de Liquidité',
      value: currentRatio.toString(),
      description: 'Couverture dettes CT',
      color: 'text-teal-600'
    }
  ];

  return (
    <Card title="Ratios Financiers Clés" className="h-fit">
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
        {metrics.map((metric, index) => (
          <div key={index} className="space-y-2 p-3 bg-gray-50 dark:bg-gray-800/50 rounded-lg border border-gray-100 dark:border-gray-700 hover:shadow-md transition-shadow duration-150">
            <div className="flex items-center justify-between">
              <div className="flex items-center min-w-0 flex-1">
                <metric.icon className={`h-4 w-4 mr-2 flex-shrink-0 ${metric.color}`} />
                <h3 className="text-xs font-medium text-gray-700 dark:text-gray-300 truncate">
                  {metric.title}
                </h3>
              </div>
            </div>
            <div className="space-y-1">
              <p className="text-lg font-bold text-gray-900 dark:text-white break-words" title={metric.value}>
                {metric.value}
              </p>
              <p className="text-xs text-gray-500 dark:text-gray-400">
                {metric.description}
              </p>
            </div>
          </div>
        ))}
      </div>
    </Card>
  );
}